import "./leafletSetup"; // Leaflet CSS + marker-icon bootstrap (moved out of the eager entry)
/* eslint-disable react/prop-types */
/**
 * Reusable "pick a point" Leaflet map. Replaces the per-component
 * `new window.google.maps.Map(...)` + click-listener boilerplate that the
 * address/location components each used to carry.
 *
 * - `position` ({lat,lng}|null) drops a draggable marker and recenters.
 * - `onPick(lat,lng)` fires on map click and on marker drag-end (when not
 *   readonly). Callers do their own reverse geocoding from there.
 * - `markers` ([{lat,lng,label?,tooltip?}]) renders read-only dots for existing
 *   points, e.g. all crowd-data submissions. `label` opens as a popup on click
 *   (only when there's no `onMarkerClick`); `tooltip` (falling back to `label`)
 *   shows on hover.
 * - `minPickZoom` gates placing a point until the map is zoomed in enough
 *   (e.g. a ~20 m view) so the location is precise.
 * - `allowPolygon` adds a Point/Polygon toggle; in polygon mode each click adds
 *   a boundary vertex (reported via `onBoundaryChange`), the caller turns those
 *   vertices into a centre point + boundary.
 * - `gatePolygon` also applies `minPickZoom` to polygon vertices (off by
 *   default, since a boundary is precise enough from its vertices alone).
 * - `showCoordinates` (on by default) shows a lat/lng readout chip for the
 *   point the user just clicked/dragged (or the latest polygon vertex), with a
 *   copy button. Pass `false` to hide it.
 */
import { useMemo, useState, useRef } from "react";
import {
  MapContainer,
  Marker,
  Popup,
  Tooltip,
  Polygon,
  Polyline,
  Rectangle,
  CircleMarker,
  ScaleControl,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import {
  MapPin,
  Hexagon,
  Undo2,
  Trash2,
  ZoomIn,
  Copy,
  Check,
} from "lucide-react";
import { MapLayers } from "./MapLayers";
import {
  ClickHandler,
  Recenter,
  InvalidateOnMount,
  ZoomWatcher,
  FlyTo,
} from "./mapHelpers";
import { PersistMapView, readMapView } from "./mapViewStore";
import { makePinIcon } from "./mapPin";

// Hyderabad, same default the app leaned on before.
const DEFAULT_CENTER = { lat: 17.385, lng: 78.4867 };

// Marker pins use the mobile PropertyPin shape (round badge + downward pointer),
// coloured by status: green = verified, amber = pending, indigo = request. The
// green/amber are the mobile app's own pin fills (CrowdTheme.green / .amber),
// which sit a shade deeper than the badge palette so a white glyph reads on top.
// The user's OWN contributions (m.mine) get a navy ring so they stand apart from
// other contributors' pins. `selected` (the hovered rail row) grows the pin.
function iconFor(m, selected) {
  // Spiderfied per-document child pins (one per document of an expanded
  // multi-document upload) read as "pick me to request" — indigo. No number chip;
  // each is connected to the parent pin by a wire and named on hover.
  if (m.isChild) {
    return makePinIcon({
      icon: "search",
      color: "#6366f1",
      iconColor: "#fff",
      selected,
    });
  }
  const color =
    m.kind === "request" ? "#6366f1" : m.verified ? "#2C9E5C" : "#E0901C";
  return makePinIcon({
    icon: m.kind === "request" ? "search" : "doc",
    color,
    iconColor: "#fff",
    selected,
    ring: m.mine ? "#153568" : null,
  });
}

export default function LocationPickerMap({
  position,
  onPick,
  readonly = false,
  zoom = 15,
  // Google imagery is sharp to 20 (see MapLayers GOOGLE_MAX_ZOOM).
  maxZoom = 20,
  minPickZoom = null,
  // When set, pinning a point flies the map to that point at THIS zoom (Crowd
  // Data passes ~70 m). Fractional zooms are honoured (a fine zoomSnap is enabled
  // automatically). Off (null) everywhere else — those maps keep the pin's zoom.
  pickZoom = null,
  pickHint = "Zoom in to select the location",
  // When an overlay (e.g. an upload modal) covers the map, the caller can hide
  // the floating zoom-gate hint so it doesn't float on top of that overlay.
  hidePickHint = false,
  className = "",
  style,
  showSearch = true,
  showLocate = true,
  // Auto-centre on the user's current location (asked once per session, then
  // reused) + drop a "you are here" marker. By default on whenever the map has
  // no point of its own yet, so an empty picker opens where the user is while a
  // saved address keeps its pin in view. Pass true/false to force it.
  autoLocate,
  // Whether the auto-locate also flies the map to the fix: a boolean, or
  // `(fix) => boolean` (see MapLayers). Default: always.
  autoLocateRecenter = true,
  // Zoom the auto-centre flies to (MapLayers default 15).
  autoLocateZoom,
  // Fires with the (debounced) "fetching your location" state so callers can
  // block card clicks while a locate is in progress.
  onLocatingChange,
  // Opt in (`pickOnLocate`) to treat pressing "my location" as CHOOSING that
  // spot: a manual fix then pins it through `onPick`, exactly like clicking it.
  // Off by default — the address pickers that share this control expect locating
  // to move the view only, and must not start selecting a location on their own.
  pickOnLocate = false,
  // Place the point on the FIRST click even when zoomed out past `minPickZoom`
  // (the map still flies in). Without this a click below the gate only zooms,
  // so nothing appears where the user clicked and the map reads as unresponsive.
  // Off by default: the contribution maps want a precise pin before anything is
  // placed.
  pickAtAnyZoom = false,
  // A human name for the pinned point (e.g. "Brodipet · Guntur"), shown as a
  // permanent label on the pin so the map itself says WHERE it is — the
  // coordinate readout alone doesn't tell anyone that. Caller-resolved, since
  // only it knows how the place should read.
  positionLabel = "",
  // The extent of the place the pin belongs to, as { south, north, west, east }.
  // Drawn as a soft dashed outline so the pin is visibly IN that area. Skipped
  // when absent, when it collapses to a point, or when it is so large that the
  // rectangle would just be screen noise (see AREA_MAX_SPAN_DEG).
  area = null,
  showCoordinates = true,
  defaultCenter = DEFAULT_CENTER,
  markers = [],
  // Transient markers rendered OUTSIDE the marker-cluster group (so they're never
  // clustered) — used for the spiderfied per-document child pins of an expanded
  // multi-document upload. Same shape as `markers` (+ `isChild`), same
  // `onMarkerClick` callback.
  extraMarkers = [],
  allowPolygon = false,
  gatePolygon = false,
  // Optional controlled draw mode ("point" | "polygon"). When provided, the
  // caller owns the mode (e.g. a Point/Polygon toggle in its own sidebar), pair
  // with `hideModeToggle` to drop the on-map buttons. Falls back to internal
  // state when omitted, so existing callers are unaffected.
  mode: modeProp,
  hideModeToggle = false,
  boundary = [],
  onBoundaryChange,
  onModeChange,
  topLeftExtra = null,
  // Offsets for the top-left controls. Callers that float something over the
  // map's top-left corner (e.g. the Maps hub's section-tab pill) push the search
  // bar and the Point/Polygon stack down so nothing is hidden behind it.
  searchTop = "10px",
  topStackTop = "5rem",
  // Centre the search bar and give it a left-hand summary section (the Maps hub
  // pairs the search with a per-tab summary in one bar).
  searchCenter = false,
  searchSummary = null,
  // When zoomed out past the pick gate, a click flies the map into that point
  // (at the gate zoom) instead of just prompting "zoom in to select".
  zoomToClick = false,
  // Move the zoom +/- control from Leaflet's default top-left to the bottom-right
  // (next to the locate button).
  zoomBottomRight = false,
  // Optional { lat, lng } the map flies to when it changes, used for
  // hover-to-locate from a list (mirrors the My Property hover-fly).
  flyTo = null,
  // Optional marker id to highlight (grow), set from the hovered rail row.
  highlightId = null,
  // Optional click handler for an existing marker (m). When set, clicking a
  // marker calls this (e.g. open a detail drawer) instead of opening its popup.
  onMarkerClick = null,
  // Base-map config. Defaults to Satellite + the mobile-style
  // Satellite⇄Cadastral toggle so EVERY location map in the app (Add / Edit
  // Property, address pickers, the Maps tab) looks the same. Callers can override
  // (e.g. baseToggle={false} for the old full layer dropdown).
  defaultBase = "Satellite",
  baseToggle = true,
  // "google" (default) | "open": the open (Esri / OpenStreetMap) set is for
  // maps where users draw boundaries, which Google forbids on its imagery.
  tiles = "google",
  // Fires when a place is chosen from the map's search box ({ lat, lng,
  // display_name }), lets the caller drop the pin + reverse-geocode there.
  onSearchSelect = null,
  // Pan to a searched place without changing zoom (default: zoom in to ~16).
  searchKeepZoom = false,
  // Maps-hub only (Crowd Data): pan this map to the place chosen in the hub
  // toolbar's single search (see MapLayers.flyOnBroadcast). Off elsewhere.
  flyOnBroadcast = false,
  // Fires on any genuine map click (control / search clicks excluded), even when
  // the click only zooms in because of the pick gate. Lets a caller react to
  // "the user clicked the map" independently of point/polygon picking.
  onAnyClick = null,
  // Maps-hub only (Crowd Data): boot on the view shared with the My Property /
  // Survey tabs and keep recording it. Off everywhere else, an address picker
  // must open on the address it's editing, not on the hub's last view.
  persistView = false,
  // Extra overlay toggles surfaced in the layer switcher (e.g. Survey Numbers).
  // Same contract as MapLayers: [{key,name}], the active keys, and a toggle
  // handler. The caller renders the actual overlay layers via `children` (any
  // react-leaflet nodes dropped inside this map's <MapContainer>).
  extraOverlays = [],
  activeExtras = [],
  onToggleExtra,
  children,
}) {
  // An area wider than this is a district or a state; outlining it would just
  // paint a box across the whole viewport, so we don't.
  const AREA_MAX_SPAN_DEG = 1.2;
  // ...and one smaller than this is a single building, where the outline would
  // sit under the pin and read as an artifact.
  const AREA_MIN_SPAN_DEG = 0.0008;

  // The searched/resolved area as Leaflet bounds, or null when there is nothing
  // worth drawing. Guarded on both ends (see the constants above) and on the
  // numbers themselves, since these arrive from a geocoder as strings.
  const areaBounds = useMemo(() => {
    if (!area) return null;
    const south = Number(area.south);
    const north = Number(area.north);
    const west = Number(area.west);
    const east = Number(area.east);
    if (![south, north, west, east].every(Number.isFinite)) return null;
    const span = Math.max(Math.abs(north - south), Math.abs(east - west));
    if (span > AREA_MAX_SPAN_DEG || span < AREA_MIN_SPAN_DEG) return null;
    return [
      [south, west],
      [north, east],
    ];
  }, [area]);
  const hasPos =
    position && Number.isFinite(position.lat) && Number.isFinite(position.lng);
  const center = hasPos ? position : defaultCenter;

  // <MapContainer> is uncontrolled: it reads centre/zoom once, at mount. Snapshot
  // the remembered hub view here so later re-renders can't fight the user's pan.
  // A caller-supplied `position` (an existing pin) still wins, it must stay framed.
  const boot = useMemo(() => {
    const saved = persistView && !hasPos ? readMapView() : null;
    return saved
      ? { center: { lat: saved.lat, lng: saved.lng }, zoom: saved.zoom }
      : { center, zoom };
    // computed once for the uncontrolled MapContainer mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "point" (drop a single pin) | "polygon" (click to add boundary vertices).
  // Controlled by `modeProp` when supplied, otherwise internal.
  const [internalMode, setInternalMode] = useState("point");
  const mode = modeProp ?? internalMode;
  // The last point the user clicked/dragged, drives the coordinate readout
  // chip immediately, before the caller's onPick round-trips back as `position`.
  const [clicked, setClicked] = useState(
    hasPos ? { lat: position.lat, lng: position.lng } : null
  );
  // Briefly flips the copy button to a check after copying the coordinates.
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);
  // Live zoom, drives the "zoom in to select" banner whenever we're zoomed out
  // past the gate (so the message shows regardless of where the user clicks).
  const [zoomLevel, setZoomLevel] = useState(boot.zoom);
  // Briefly emphasises the gate hint right after the user clicks while still
  // zoomed out (the click can't place a point yet, so we flash the message as
  // feedback instead of doing nothing silently).
  const [flash, setFlash] = useState(false);
  const flashTimer = useRef(null);
  const flashHint = () => {
    setFlash(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(false), 1800);
  };

  const verts = (boundary || []).filter(
    (v) => Number.isFinite(v?.lat) && Number.isFinite(v?.lng)
  );

  const switchMode = (m) => {
    if (m === mode) return;
    if (modeProp == null) setInternalMode(m);
    onModeChange?.(m);
  };

  const handleMapClick = (lat, lng, meta) => {
    if (mode === "polygon") onBoundaryChange?.([...verts, { lat, lng }]);
    else {
      setClicked({ lat, lng });
      onPick?.(lat, lng, meta);
    }
  };

  const copyCoords = (p) => {
    navigator.clipboard?.writeText(`${p.lat}, ${p.lng}`);
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  // The zoom gate normally applies to single-pin picking only, a polygon's
  // precision comes from its vertices, so by default it can be drawn at any
  // zoom. Callers that want vertices placed precisely too opt in with
  // `gatePolygon`, which makes polygon clicks honour `minPickZoom` as well.
  const gateZoom = mode === "polygon" && !gatePolygon ? null : minPickZoom;
  // The "zoom in to select" banner is only true when a click really can't place
  // a point — with `pickAtAnyZoom` it can, so the banner would be a lie.
  const tooFar = gateZoom != null && zoomLevel < gateZoom && !pickAtAnyZoom;

  // What the coordinate readout shows: in polygon mode, the latest vertex; in
  // point mode, the current pin (`position`) or the last click as a fallback.
  const readoutPoint =
    mode === "polygon"
      ? verts[verts.length - 1] || null
      : hasPos
      ? position
      : clicked;

  return (
    <div className={className} style={{ position: "relative", ...style }}>
      {/* The pin's name label. Leaflet's default tooltip is a pale box with a
          border; this restyles it as the app's navy chip so the map reads like
          the rest of the product. Scoped to our own class, so every other
          tooltip on the map (marker hovers) is untouched. */}
      <style>{`
        .leaflet-tooltip.jg-pin-label {
          background: #153568;
          color: #fff;
          border: none;
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 11px;
          font-weight: 700;
          /* OSM names run long ("Greater Hyderabad Municipal Corporation West
             Zone"), and a single clipped line told the visitor almost nothing.
             Two lines, then clip. */
          white-space: normal;
          /* Size to the text up to the cap, then wrap — without this the box
             collapses to a narrow column instead of using the width it has. */
          width: max-content;
          max-width: 260px;
          line-height: 1.35;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-align: center;
          box-shadow: 0 6px 18px -6px rgba(21,53,104,0.7);
        }
        .leaflet-tooltip.jg-pin-label::before { border-top-color: #153568; }
      `}</style>
      <MapContainer
        center={[boot.center.lat, boot.center.lng]}
        zoom={boot.zoom}
        maxZoom={maxZoom}
        // Allow fractional zoom levels when a fractional pickZoom is used (Crowd
        // Data's ~70 m ≈ zoom 17.75), so the fly-to doesn't snap to a whole level.
        zoomSnap={
          pickZoom != null && !Number.isInteger(pickZoom) ? 0.25 : undefined
        }
        scrollWheelZoom
        zoomControl={!zoomBottomRight}
        style={{ width: "100%", height: "100%" }}
      >
        <MapLayers
          showSearch={showSearch && !readonly}
          showLocate={showLocate}
          showZoom={zoomBottomRight}
          autoLocateRecenter={autoLocateRecenter}
          autoLocateZoom={autoLocateZoom}
          autoLocate={
            autoLocate ??
            (!readonly && !(Number.isFinite(position?.lat) && Number.isFinite(position?.lng)))
          }
          onLocatingChange={onLocatingChange}
          onLocate={
            pickOnLocate && !readonly
              ? (lat, lng) => onPick?.(lat, lng, { source: "locate" })
              : undefined
          }
          locateZoom={minPickZoom != null ? maxZoom : undefined}
          searchTop={searchTop}
          searchCenter={searchCenter}
          searchSummary={searchSummary}
          defaultBase={defaultBase}
          baseToggle={baseToggle}
          tiles={tiles}
          persistBase={persistView}
          flyOnBroadcast={flyOnBroadcast}
          onSearchSelect={onSearchSelect}
          searchKeepZoom={searchKeepZoom}
          extraOverlays={extraOverlays}
          activeExtras={activeExtras}
          onToggleExtra={onToggleExtra}
        />
        <PersistMapView enabled={persistView} />
        {/* Caller-supplied overlay layers toggled from the layer switcher. */}
        {children}
        <InvalidateOnMount />
        <ScaleControl position="bottomleft" imperial={false} />
        <ZoomWatcher onZoom={setZoomLevel} />
        {/* Don't auto-recenter while drawing a polygon (would jump on each vertex).
            With `pickZoom` (Crowd Data), pinning a point flies to it at that zoom
            (~70 m); otherwise it just recenters at the current zoom (unchanged). */}
        <Recenter
          center={mode === "point" && hasPos ? position : null}
          zoom={mode === "point" && pickZoom != null ? pickZoom : undefined}
          fly={pickZoom != null}
        />
        {/* Hover-to-locate: fly to the list item the user is hovering. */}
        <FlyTo point={flyTo} />
        {!readonly && (
          <ClickHandler
            onClick={handleMapClick}
            minZoom={gateZoom}
            onTooFar={flashHint}
            zoomOnTooFar={zoomToClick}
            pickOnTooFar={pickAtAnyZoom}
            onAnyClick={onAnyClick}
          />
        )}

        {markers.length > 0 && (
          <MarkerClusterGroup
            chunkedLoading
            maxClusterRadius={80}
            showCoverageOnHover={false}
          >
            {markers
              .filter((m) => Number.isFinite(m?.lat) && Number.isFinite(m?.lng))
              .map((m, i) => {
                // Highlight the hovered rail row's marker, by id, or by matching
                // coordinates (rail rows + map markers come from different
                // endpoints, so the id format may differ).
                const sel =
                  (highlightId != null && m.id === highlightId) ||
                  (flyTo &&
                    Math.abs(m.lat - flyTo.lat) < 1e-5 &&
                    Math.abs(m.lng - flyTo.lng) < 1e-5);
                return (
                <Marker
                  key={m.id || i}
                  position={[m.lat, m.lng]}
                  icon={iconFor(m, sel)}
                  zIndexOffset={sel ? 1000 : 0}
                  eventHandlers={
                    onMarkerClick ? { click: () => onMarkerClick(m) } : undefined
                  }
                >
                  {/* Hover label. Markers that open a drawer on click (Crowd Data)
                      have no popup to name them, so the tooltip is the only way to
                      tell one pin from another without clicking. `tooltip` is the
                      short form (just the document name); `label` is the fuller
                      popup line, used as a fallback. */}
                  {(m.tooltip || m.label) && (
                    <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                      {m.tooltip || m.label}
                    </Tooltip>
                  )}
                  {m.label && !onMarkerClick && <Popup>{m.label}</Popup>}
                </Marker>
                );
              })}
          </MarkerClusterGroup>
        )}

        {/* Spider legs: a wire from the parent pin (anchor) to each child pin so
            the expansion reads as "these documents belong to this point". Drawn
            before the child markers so the pins sit on top of the lines. */}
        {extraMarkers.length > 0 &&
          extraMarkers
            .filter(
              (m) =>
                Number.isFinite(m?.lat) &&
                Number.isFinite(m?.lng) &&
                m.anchor &&
                Number.isFinite(m.anchor.lat) &&
                Number.isFinite(m.anchor.lng)
            )
            .map((m, i) => (
              <Polyline
                key={`leg-${m.id || i}`}
                positions={[
                  [m.anchor.lat, m.anchor.lng],
                  [m.lat, m.lng],
                ]}
                pathOptions={{
                  color: "#6366f1",
                  weight: 1.5,
                  opacity: 0.75,
                  dashArray: "3 4",
                }}
              />
            ))}

        {/* Spiderfied per-document child pins for an expanded multi-document
            upload — rendered OUTSIDE the cluster group so the manual ring spread
            isn't re-clustered. They sit above every other marker (zIndexOffset). */}
        {extraMarkers.length > 0 &&
          extraMarkers
            .filter((m) => Number.isFinite(m?.lat) && Number.isFinite(m?.lng))
            .map((m, i) => (
              <Marker
                key={m.id || `x-${i}`}
                position={[m.lat, m.lng]}
                icon={iconFor(m, false)}
                zIndexOffset={2000}
                eventHandlers={
                  onMarkerClick ? { click: () => onMarkerClick(m) } : undefined
                }
              >
                {(m.tooltip || m.label) && (
                  <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                    {m.tooltip || m.label}
                  </Tooltip>
                )}
              </Marker>
            ))}

        {/* Polygon being drawn: filled shape + a dot on every vertex. */}
        {verts.length >= 2 && (
          <Polygon
            positions={verts.map((v) => [v.lat, v.lng])}
            pathOptions={{
              color: "#1c4b93",
              weight: 2,
              fillColor: "#1c4b93",
              fillOpacity: 0.15,
            }}
          />
        )}
        {verts.map((v, i) => (
          <CircleMarker
            key={`v-${i}`}
            center={[v.lat, v.lng]}
            radius={5}
            pathOptions={{
              color: "#ffffff",
              weight: 2,
              fillColor: "#1c4b93",
              fillOpacity: 1,
            }}
          />
        ))}

        {areaBounds && (
          <Rectangle
            bounds={areaBounds}
            interactive={false}
            // Outline only, NO fill. A searched city's extent is far larger
            // than a street-level viewport, so a filled rectangle would tint the
            // whole map with its edges off-screen — which reads as "the map
            // looks washed out", not as "here is the area". Unfilled, it simply
            // shows nothing until the user zooms out far enough to meet a
            // boundary, which is exactly when it means something.
            pathOptions={{
              color: "#1c4b93",
              weight: 1.5,
              dashArray: "6 5",
              fill: false,
            }}
          />
        )}

        {hasPos && (
          <Marker
            position={[position.lat, position.lng]}
            draggable={!readonly && mode === "point"}
            eventHandlers={{
              dragend: (e) => {
                const ll = e.target.getLatLng();
                setClicked({ lat: ll.lat, lng: ll.lng });
                onPick?.(ll.lat, ll.lng);
              },
            }}
          >
            {positionLabel && (
              <Tooltip permanent direction="top" offset={[0, -38]} className="jg-pin-label">
                {positionLabel}
              </Tooltip>
            )}
          </Marker>
        )}
      </MapContainer>

      {/* Top-left control stack, Point/Polygon toggle, drawing controls, and
          any caller-supplied extras (e.g. the Upload button). Placed below the
          zoom control + search bar so it isn't hidden behind them. */}
      {(allowPolygon || topLeftExtra) && !readonly && (
        <div
          className="absolute left-3 z-[1100] flex max-w-[14rem] flex-col items-start gap-2"
          style={{ top: topStackTop }}
        >
          {allowPolygon && (
            <>
          {!hideModeToggle && (
          <div className="inline-flex overflow-hidden rounded-lg bg-white shadow ring-1 ring-black/10">
            <button
              type="button"
              onClick={() => switchMode("point")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition ${
                mode === "point"
                  ? "bg-[#1c4b93] text-white"
                  : "text-[#153568] hover:bg-slate-100"
              }`}
            >
              <MapPin className="h-3.5 w-3.5" /> Point
            </button>
            <button
              type="button"
              onClick={() => switchMode("polygon")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition ${
                mode === "polygon"
                  ? "bg-[#1c4b93] text-white"
                  : "text-[#153568] hover:bg-slate-100"
              }`}
            >
              <Hexagon className="h-3.5 w-3.5" /> Polygon
            </button>
          </div>
          )}
            </>
          )}
          {topLeftExtra}
        </div>
      )}

      {/* Polygon drawing hint, top-centre (below the search bar) so it reads as
          a clean banner instead of crowding the corner. z-[900] keeps it above
          the map but BELOW the search control (Leaflet controls sit at z-1000), so
          the location-search results dropdown overlays the banner instead of
          being hidden behind it. */}
      {allowPolygon && mode === "polygon" && !readonly && (
        <div className="pointer-events-none absolute inset-x-0 top-16 z-[900] flex justify-center px-3">
          <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-[11px] shadow-lg ring-1 ring-black/10 backdrop-blur">
            <span className="font-medium text-[#153568]">
              Click the map to add boundary points
              {verts.length > 0 && (
                <span className="ml-1 text-slate-400">
                  ({verts.length} pt{verts.length > 1 ? "s" : ""})
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => onBoundaryChange?.(verts.slice(0, -1))}
              disabled={verts.length === 0}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
            >
              <Undo2 className="h-3 w-3" /> Undo
            </button>
            <button
              type="button"
              onClick={() => onBoundaryChange?.([])}
              disabled={verts.length === 0}
              className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-2 py-0.5 font-medium text-rose-600 transition hover:bg-rose-50 disabled:opacity-40"
            >
              <Trash2 className="h-3 w-3" /> Clear
            </button>
          </div>
        </div>
      )}

      {/* Zoom-in gate hint, visible whenever we're zoomed out past the gate
          (and flashed when a too-far click is attempted). Sits above the search
          control (z-1200) and below the top control row so it's never hidden. */}
      {!readonly && !hidePickHint && (tooFar || flash) && (
        <div className="pointer-events-none absolute inset-x-0 top-16 z-[1300] flex justify-center px-3">
          <span
            className={`pointer-events-auto inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-lg backdrop-blur transition ${
              flash ? "bg-rose-600 ring-2 ring-rose-300 scale-105" : "bg-[#153568]/90"
            }`}
          >
            <ZoomIn className="h-3.5 w-3.5 text-[#f0a500]" />
            {pickHint}
          </span>
        </div>
      )}

      {/* Coordinate readout, shows the lat/lng of the point the user just
          clicked/dragged. Bottom-centre so it clears the scale control
          (bottom-left) and the OSM attribution (bottom-right). */}
      {showCoordinates && !readonly && readoutPoint && (
        <div className="pointer-events-none absolute inset-x-0 bottom-6 z-[1100] flex justify-center px-3">
          <div className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-[#153568]/90 px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-lg backdrop-blur">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-[#f0a500]" />
            <span className="tabular-nums tracking-tight">
              {readoutPoint.lat.toFixed(6)}, {readoutPoint.lng.toFixed(6)}
            </span>
            <button
              type="button"
              onClick={() => copyCoords(readoutPoint)}
              title="Copy coordinates"
              className="ml-0.5 inline-flex items-center rounded-full p-1 text-white/80 transition hover:bg-white/15 hover:text-white"
            >
              {copied ? (
                <Check className="h-3 w-3 text-emerald-300" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
